import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLeaveBalance } from "../../contexts/LeaveBalanceContext";
import Modal from "../../components/UI/Modal";
import LeaveBalanceForm from "../../components/Form/LeaveBalanceForm";
// import BulkHolidayForm from "../../components/Form/BulkHolidayForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import Drawer from "../../components/UI/Drawer";
import { getToken } from "../../utils/tokenUtils";
import { X, Briefcase, MoreVertical, Building, Calendar, Users, MapPin, Award, Clock, Grid } from 'lucide-react';
import DropdownMenu from "../../components/UI/DropdownMenu";

// Separate component for actions column to avoid hooks rule violation
const ActionsColumn = React.memo(({ row, openMenu, setOpenMenu, openModalForEdit, handleLeaveBalance }) => {
  const buttonRef = useRef(null);
  
  return (
    <div className="flex items-center justify-center relative">
      <button
        ref={buttonRef}
        onClick={() =>
          setOpenMenu(openMenu === row.id ? null : row.id)
        }
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
      </button>

      {openMenu === row.id && (
        <DropdownMenu
          buttonRef={buttonRef}
          onClose={() => setOpenMenu(null)}
        >
          <button
            onClick={(e) => {
              openModalForEdit(row);
              setOpenMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Edit
          </button>
          <DeleteButton
            style={`text-red-600 hover:text-red-800 px-3 py-2`}
            onDelete={(e) => {
              handleLeaveBalance(row.id);
              setOpenMenu(null);
            }}
          />
        </DropdownMenu>
      )}
    </div>
  );
});

const HolidayMasters = React.memo(() => {
  const { leaveBalances, fetchLeaveBalances, addLeaveBalance, handleBulkLeaveCreate, updateLeaveBalance, deleteLeaveBalance, pagination, loading } = useLeaveBalance();

  // const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingLeaveBalance, setEditingLeaveBalance] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);
  const token = getToken();
  const [openMenu, setOpenMenu] = useState(null);
    





  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingLeaveBalance(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  // const openModalForBulkAdd = useCallback(() => {
  //   setShowModal(true);
  // }, []);

  const openModalForEdit = useCallback((leaveBalance) => {
    // console.log("edit leave: ", leaveBalance)
    setEditingLeaveBalance(leaveBalance);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingLeaveBalance(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    return Object.fromEntries(formData.entries());
  };

  // create and update function
  const handleFormSubmit = useCallback(
    async (leaveBalance) => {
      // console.log("leave balance in handle form submit: ", leaveBalance)

      // setLoading(true);
      try {
        if (editingLeaveBalance) {
          // console.log("leave balance payload", leaveBalance);
        
          // Convert FormData to object
          // console.log(leaveBalance instanceof FormData)
          const leaveBalanceData = leaveBalance instanceof FormData ? convertFormDataToObject(leaveBalance) : leaveBalance;
  
          // console.log("Payload being sent:", { ...editingLeaveBalance, ...leaveBalanceData });
  
          const result = await updateLeaveBalance({ ...editingLeaveBalance, ...leaveBalanceData });
          // console.log("result in leave balance update: ", result)
          if (result?.success === true) {
            // setNotification("Leave balance updated successfully.");
            setNotification({
              message: "Leave balance updated successfully.",
              type: "success",
            });
            closeModal();
          } else {
            // setNotification(`Error: ${result?.message || "Failed to update leave balance."}`);
            setNotification({
              message: `Error: ${result?.message || "Failed to update leave balance."}`,
              type: "error",
            });
          }
          // setMessage("Holiday updated successfully.");
          // setNotification("Holiday updated successfully.");
        } else {
          // console.log("leave balance payload in else part: ", leaveBalance)
          const result = await addLeaveBalance(leaveBalance);
          // // console.log("error in leave balance: ", result)
          if (result?.success === true) {
            // setNotification("Leave balance created successfully.");
            setNotification({
              message: "Leave balance created successfully.",
              type: "success",
            });
            closeModal();
          } else {
            // If validation errors are present
            if (result?.message?.errors) {
              const errorMessages = Object.entries(result.message.errors)
                .map(([field, msgs]) => `${field}: ${msgs.join(", ")}`)
                .join("; ");
              // setNotification(`Error: ${errorMessages}`);
              setNotification({
                message: `Error: ${errorMessages}`,
                type: "error",
              });
            } else {
              // console.log("result in else part: ", result)
              // setNotification(`Error: ${result?.message.error || "Failed to add leave balance."}`);
              setNotification({
                message: `Error: ${result?.message.error || "Failed to add leave balance."}`,
                type: "error",
              });
            }
          }
        }
        // closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
          // setNotification(err.message);
          setNotification({
            message: err.message,
            type: "error",
          });
        } else {
          // console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: "Unexpected error during submission:", err,
            type: "error",
          });
        }
      } finally {
        // setLoading(false);
      }
    },
    [editingLeaveBalance, addLeaveBalance, updateLeaveBalance, closeModal]
  );

 
  // Memoize holidays names
  const leaveBalanceForTable = useMemo(
    () =>
      leaveBalances.map((leaveBalance) => ({
        ...leaveBalance,
        compulsory_national_holiday_balance: leaveBalance?.compulsory_national_holiday_balance || "-",
        public_holiday_balance: leaveBalance?.public_holiday_balance || "-",
        privileged_leave_balance: leaveBalance?.privileged_leave_balance || "-",
        casual_leave_balance: leaveBalance?.casual_leave_balance || "-",
        total_leave_balance: leaveBalance?.total_leave_balance || "-",
        leave_taken: leaveBalance?.leave_taken || "-",
        leave_balance_remaining: leaveBalance?.leave_balance_remaining || "-",
        carry_forward_balance: leaveBalance?.carry_forward_balance || "-",
        is_on_notice: leaveBalance?.is_on_notice,
    })),
    [leaveBalances]
  );

  // delete function
  const handleLeaveBalance = async (id) => {
    try {
      await deleteLeaveBalance(id);
      // setNotification("leave balance deleted successfully");
      setNotification({
        message: "leave balance deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete leave balance");
      setNotification({
        message: "Failed to delete leave balance",
        type: "error",
      });
    }
  };


  // bulk create function
  const handleBulkLeaveBalance = async () => {
    try {
      const response = await handleBulkLeaveCreate();
      // // console.log("response in bulk leave balance: ", response.success)
      // setNotification("Bulk leave balance created successfully");
      setNotification({
        message: "Bulk leave balance created successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to create bulk leave balance");
      setNotification({
        message: "Failed to create bulk leave balance",
        type: "error",
      });
    }
  };
  

  // columns for table
  const columns = useMemo(
    () => [
      { label: "Employee ID", field: "employee_id" },
      { label: "National Holiday", field: "compulsory_national_holiday_balance" },
      { label: "Public Holiday", field: "public_holiday_balance" },
      { label: "Privileged Leave", field: "privileged_leave_balance" },
      { label: "Casual Leave", field: "casual_leave_balance" },
      { label: "Total Leaves", field: "total_leave_balance" },
      { label: "Leaves Taken", field: "leave_taken" },
      { label: "Remaining Leaves", field: "leave_balance_remaining" },
      { label: "Carry-forward Leaves", field: "carry_forward_balance" },
      { label: "Notice Period", field: "is_on_notice", render: (row) => (row.is_on_notice === 1 ? "Yes" : "No"), },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <ActionsColumn
            row={row}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
            openModalForEdit={openModalForEdit}
            handleLeaveBalance={handleLeaveBalance}
          />
        ),
      },
    ],
    [
      openMenu,
      openModalForEdit,
      handleLeaveBalance,
    ]
  );
  
  // Fetch leave balances when the component mounts or when the page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchLeaveBalances(newPage);
    }
  };

  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      
      {/* notification compo */}
      {notification && (
          <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* title */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-2xl font-semibold text-black dark:text-white">Leave Balance Masters</h2>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <button
                  onClick={openModalForAdd}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
                  >
                  Add Leave Balance
              </button>
              <button
                onClick={handleBulkLeaveBalance}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 w-full sm:w-auto"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Bulk Leave Balance
              </button>
          </div>
      </div>

      {/* Success Message */}
      {message && (
        <div className="mb-4 p-4 bg-green-100 border border-green-500 text-green-700 rounded-md">
            {message}
        </div>
      )}

      {/* Leave Balance Table */}
      <ReusableTable
        data={leaveBalanceForTable}
        columns={columns}
        filterable={true}
        excludeFilterColumns={["actions"]}
        onFilterChange={(filtering) => setIsFiltering(filtering)}
      />

      {/* Pagination */}
      {!isFiltering && leaveBalanceForTable?.length > 0 && (
          <ReactPaginate
          breakLabel="..."
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          pageCount={pagination.totalPages}
          renderOnZeroPageCount={null}
          nextLabel="Next >"
          previousLabel="< Prev"
          previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800'
          nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800"
          containerClassName="flex items-center justify-center mt-4"
          pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
          />
      )}

      {/* Modal for Adding or Editing */}
      <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          title={editingLeaveBalance ? "Edit Leave Balance" : "Add Leave Balance"}>

          <LeaveBalanceForm
            // loading={loading}
            closeModal={closeModal}
            onSubmit={handleFormSubmit}
            initialValues={editingLeaveBalance || { user_id: "", 
                                                    compulsory_national_holiday_balance: "",
                                                    public_holiday_balance: "", 
                                                    privileged_leave_balance: "",
                                                    casual_leave_balance: "",
                                                    is_on_notice: "" }}
            errors={formErrors}
          />
      </Modal>
    
      {/* drawer for leave balance details */}
      <Drawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} ChilDiv="overflow-auto" ChildParent="" height_width="md:max-w-3xl md:h-max-h-3xl" border_radius="md:rounded-3xl">
        {selectedRequest && (
          <div className="p-4">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6 dark:text-white">Leave Balance Details</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">User</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.user_name}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">Compulsory Holiday</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.compulsory_national_holiday_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">Public Holiday</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.public_holiday_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">PL</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.privileged_leave_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500">CL</span> 
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.casual_leave_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">Total Leaves</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.total_leave_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">Leave Taken</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.leave_taken}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">Leave Remaining</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.leave_balance_remaining}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">Carry-forward Balance</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.carry_forward_balance}</div>
              </div>

              <div className="bg-gray-100 dark:bg-black p-4 rounded-lg shadow-sm">
                <span className="text-gray-500 dark:text-white font-sans font-semibold">On Notice Period</span>
                <div className="text-gray-900 dark:text-gray-400 font-medium">{selectedRequest.is_on_notice ? "Yes" : "No"}</div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
});

export default HolidayMasters;
